import React from 'react';
import { useState, useEffect } from 'react';
import firebase from '../../config/firebaseConfig';
import ReactQuill from 'react-quill';
import { Container, Card, Row, Col, Button, Form, Toast, ToastContainer, Spinner, Modal } from 'react-bootstrap';
import SidebarDashboard from './SidebarDashboard';
import '@animxyz/core';
import { XyzTransition } from '@animxyz/react';
import 'react-quill/dist/quill.snow.css';
import { MODULES } from '../../config/toolbar';

const ListeNotes = () => {
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  const currentYear = new Date().getFullYear();

  const triggerToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  useEffect(() => {

    const unsubscribeAuth = firebase.auth().onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);

        const unsubscribeSnapshot = firebase.firestore().collection('notes')
          .where('userId', '==', user.uid)
          .orderBy('createdAt', 'desc')
          .onSnapshot((snapshot) => {
            const notesData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setNotes(notesData);
            setLoading(false);
          }, (error) => {
            console.error("Erreur Firestore : ", error);
            setLoading(false);
          });

        return () => unsubscribeSnapshot();
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;

    try {
      await firebase.firestore().collection('notes').add({
        title,
        content,
        userId: userId,
        createdAt: new Date()
      });

      setTitle('');
      setContent('');
      triggerToast("Note ajoutée !", 'success');
    } catch (error) {
      triggerToast("Erreur lors de l'ajout", 'danger');
    }
  };

  const handleShowModal = (id) => {
    setNoteToDelete(id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setNoteToDelete(null);
    setShowModal(false);
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const handleDelete = async () => {
    if (!noteToDelete) return;

    try {
      await delay(500);
      await firebase.firestore().collection('notes').doc(noteToDelete).delete();
      triggerToast("Note supprimée avec succès !", 'success');
    } catch (error) {
      triggerToast("Erreur lors de la suppression", 'danger');
    } finally {
      handleCloseModal();
    }
  };


  const formatFirebaseDate = (timestamp) => {
    if (!timestamp) return "Date inconnue";

    if (typeof timestamp.toDate === 'function') {
      const date = timestamp.toDate();
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    return "Format invalide";
  };

  return (
    < React.Fragment >
      <Container fluid className='min-vh-100 text-light py-4 mt-2 my-0'>
        <div className="d-flex flex-column flex-md-row">
          <SidebarDashboard />
          <div className="admin-main-wrapper w-100">
            <Container className="py-1 bg-profil min-vh-100">
              <XyzTransition appear xyz='fade stagger delay-4 duration-15'>
                <Container className="my-4 px-3 px-sm-3 my-sm-3">
                  <h3 className="text-center text-warning fw-semibold mb-3 fs-sm-2">📝 Mes Notes {currentYear}</h3>
                  <Card className="w-100 bg-dark mx-auto mt-2 p-1 mb-4 shadow-sm border-secondary">
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-2">
                        <div className="border border-secondary rounded bg-dark">
                          <Form.Control
                            required
                            type="text"
                            className='notewrite'
                            placeholder="Titre de la note..."
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            style={{ outline: 'none', boxShadow: 'none' }}
                          />
                        </div>
                      </Form.Group>

                      <Form.Group className="mb-2">
                        <ReactQuill
                          theme="snow"
                          value={content}
                          onChange={setContent}
                          modules={MODULES}
                          placeholder="Commencez à écrire votre texte ici..."
                        />
                      </Form.Group>
                      <div className="justify-content-end">
                        <Button variant="secondary" type="submit" disabled={loading} className="d-flex justify-content-center mx-auto mb-2 rounded w-sm-auto">
                          Ajouter la note
                      </Button>
                      </div>
                    </Form>
                  </Card>

                  {loading ? (
                    <div className="text-center"><Spinner animation="border" variant="primary" /></div>
                  ) : (
                      <Row>
                        {notes.map((note, index) => {
                          const isLastNote = index === notes.length - 1;

                          return (
                            <Col xs={12} key={note.id} className="mb-3">
                              <XyzTransition appear xyz='fade stagger delay-2 duration-10'>
                                <Card className="shadow-sm" style={isLastNote ? { marginBottom: '40px' } : {}}>
                                  <Card.Body className="d-flex flex-column justify-content-between p-1 mb-1 profile-body">
                                    <div className="d-flex justify-content-center w-100">
                                      <h4 className="mb-2 text-dark">{note.title}</h4>
                                    </div>
                                    <div
                                      className="text-start text-dark note-content-display w-100"
                                      style={{ whiteSpace: 'pre-wrap' }}
                                      dangerouslySetInnerHTML={{ __html: note.content }}
                                    />

                                    <div className="mt-auto d-flex justify-content-between align-items-end pt-2 px-3 mb-1 border-top responsive-note-footer w-100">
                                      <div className="mt-auto text-center pt-2 mb-0 fs-5">
                                        <small className="badge bg-light text-muted p-2 responsive-note-badge">
                                          📅 {formatFirebaseDate(note.createdAt)}
                                        </small>
                                      </div>
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        className="d-inline-flex align-items-center justify-content-center btn-danger responsive-trash-btn"
                                        style={{ height: '30px', width: '30px', borderRadius: '6px' }}
                                        onClick={() => handleShowModal(note.id)}
                                      >
                                        <i className="bi bi-trash3 fs-6"></i>
                                      </Button>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </XyzTransition>
                            </Col>
                          );
                        })}
                      </Row>
                    )}
                </Container>
              </XyzTransition>

              <Modal show={showModal} onHide={handleCloseModal} centered backdrop="static" keyboard={false}>
                <Modal.Header closeButton closeVariant="dark" className="text-danger border-secondary py-2">
                  <Modal.Title className="m-0 text-danger fw-semibold" style={{ fontSize: '1.2rem' }}>
                    Suppression de la note
                </Modal.Title>
                </Modal.Header>
                <Modal.Body className='text-center fw-semibold p-2' style={{ fontSize: '1.1rem' }}>
                  Voulez-vous supprimer cette note ?
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-end border-top-0 pt-3">
                  <Button variant="secondary" onClick={handleCloseModal}>
                    Annuler
                  </Button>
                  <Button variant="danger" onClick={handleDelete}>
                    Supprimer
                 </Button>
                </Modal.Footer>
              </Modal>

              <ToastContainer position="bottom-end" className="py-5" style={{ position: 'fixed', top: '15px', zIndex: 9999 }}>
                <Toast
                  onClose={() => setShowToast(false)}
                  show={showToast}
                  delay={3000}
                  autohide
                  bg={toastVariant}
                  className="text-white shadow-lg border-0"
                >
                  <div className="d-flex align-items-center p-2">
                    <Toast.Body className="d-flex justify-content-between align-items-center py-2 px-3">
                      <i className="material-icons me-2" style={{ verticalAlign: 'middle' }}>
                        {toastVariant === 'success' ? 'check_circle' : 'error'}
                      </i>
                      <span className="fw-semibold" style={{ letterSpacing: '0.2px' }}>{toastMessage}</span>
                    </Toast.Body>
                    <Button
                      variant="close"
                      variant-white="true"
                      className="me-2 m-auto btn-close-white"
                      onClick={() => setShowToast(false)}
                      aria-label="Fermer"
                    />
                  </div>
                </Toast>
              </ToastContainer>
            </Container>
          </div>
        </div>
      </Container>
    </React.Fragment >
  );
};

export default ListeNotes;